/**
 * PowerReceiptsPage Component
 * Standalone page for receipt of powers confirmation
 * - User view: Shows pending status and confirm button
 * - Admin view: Code entry to verify receipts + list of pending orders
 */

import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import {
  CheckCircleIcon,
  InboxArrowDownIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  BuildingOfficeIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { useOathAuth } from '../../../services/oathAuthContext';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathButton,
  OathEmptyState,
  OathAlert,
  OathSpinner,
  OathDualSpinner
} from '../../shared/OathUI';
import { SortableHeader, Pagination, MobileLoadingSkeleton, TableLoadingSkeleton, withErrorBoundary } from './components/shared';
import { formatDate, formatCurrency, getPowerStatusBadgeInfo } from './utils/formatters';
import { PAGINATION_OPTIONS, RECEIPT_VALIDATION, RECEIPT_MESSAGES, RECEIPT_FEATURES } from './utils/constants';
import { useIsMobile } from './utils/hooks';
import { usePermissions } from '../../../hooks/usePermissions';
import { toast } from 'react-toastify';
import {
  getReceiptPendingStatus,
  getReceiptOrders,
  lookupReceiptByCode,
  confirmPowerReceipt
} from '../../../services/oathPowerReportService';
import ReceiptConfirmModal from './modals/ReceiptConfirmModal';
import ReceiptOrderDetailModal from './modals/ReceiptOrderDetailModal';

// Memoized table row component
const OrderTableRow = memo(({ order, onClick }) => (
  <tr
    onClick={RECEIPT_FEATURES.ORDER_DRILLDOWN ? () => onClick(order) : undefined}
    className={RECEIPT_FEATURES.ORDER_DRILLDOWN
      ? 'cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700'
      : 'transition-colors'}
  >
    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
      {order.agencyName}
    </td>
    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
      {order.orderNumber}
    </td>
    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
      {formatDate(order.shipDate)}
    </td>
    {RECEIPT_FEATURES.SHOW_POWER_COUNT && (
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">
        {order.powerCount}
      </td>
    )}
    <td className="px-4 py-3 text-center">
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
        Pending
      </span>
    </td>
  </tr>
));
OrderTableRow.displayName = 'OrderTableRow';

// Memoized mobile card component
const OrderMobileCard = memo(({ order, onClick }) => (
  <div
    onClick={RECEIPT_FEATURES.ORDER_DRILLDOWN ? () => onClick(order) : undefined}
    className={RECEIPT_FEATURES.ORDER_DRILLDOWN
      ? 'p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors'
      : 'p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors'}
  >
    <div className="flex justify-between items-start mb-2">
      <h3 className="font-medium text-gray-900 dark:text-white">
        {order.agencyName}
      </h3>
      <span className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded">
        Pending
      </span>
    </div>
    <div className="grid grid-cols-2 gap-2 text-sm">
      <div>
        <span className="text-gray-500 dark:text-gray-400">Order #:</span>
        <span className="ml-1 text-gray-900 dark:text-white">{order.orderNumber}</span>
      </div>
      {RECEIPT_FEATURES.SHOW_POWER_COUNT && (
        <div>
          <span className="text-gray-500 dark:text-gray-400">Powers:</span>
          <span className="ml-1 text-gray-900 dark:text-white">{order.powerCount}</span>
        </div>
      )}
      <div>
        <span className="text-gray-500 dark:text-gray-400">Assigned:</span>
        <span className="ml-1 text-gray-900 dark:text-white">{formatDate(order.shipDate)}</span>
      </div>
    </div>
  </div>
));
OrderMobileCard.displayName = 'OrderMobileCard';

const PowerReceiptsPage = () => {
  const { isLoading: authLoading } = useOathAuth();
  const isMobile = useIsMobile();
  const { isAdmin } = usePermissions();

  // User view state
  const [pendingStatus, setPendingStatus] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState(null);

  // Admin view - code input state
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const inputRef = useRef(null);

  // Admin view - orders list state
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [ordersError, setOrdersError] = useState(null);
  const [sortField, setSortField] = useState('shipDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedFilterAgencyId, setSelectedFilterAgencyId] = useState(null);
  const [agenciesWithCounts, setAgenciesWithCounts] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGINATION_OPTIONS.PAGE_SIZE,
    totalCount: 0
  });

  // Modal state (for regular user flow)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Admin view - detail modal + admin-initiated confirm modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [adminConfirmContext, setAdminConfirmContext] = useState(null);

  // AbortController ref for request cancellation
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const initialLoadDoneRef = useRef(false);

  // Validate code format
  const isValidCode = useCallback((code) => {
    if (RECEIPT_VALIDATION.CODE_PATTERN.test(code)) {
      return true;
    }
    if (code.toLowerCase().startsWith('code-') && code.length >= 6) {
      return true;
    }
    return false;
  }, []);

  const codeIsValid = isValidCode(confirmationCode);
  const shouldMaskInput = confirmationCode.toLowerCase().startsWith(RECEIPT_VALIDATION.ADMIN_CODE_PREFIX);

  // Load pending status for regular users
  const loadPendingStatus = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const currentRequestId = ++requestIdRef.current;

    setIsLoadingStatus(true);
    setStatusError(null);
    try {
      const result = await getReceiptPendingStatus();
      if (currentRequestId !== requestIdRef.current) return;

      if (result.success) {
        setPendingStatus(result.data);
      } else {
        setStatusError(result.error || 'Failed to load pending status');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (currentRequestId === requestIdRef.current) {
        setStatusError('Network error. Please try again.');
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoadingStatus(false);
      }
    }
  }, []);

  // Load orders for admin view
  // Load the full dataset so sort + pagination can be done client-side across
  // every order (instead of only sorting the current page). The backend endpoint
  // doesn't support sort params, so we fetch up to a high ceiling and page locally.
  const FULL_LOAD_PAGE_SIZE = 1000;
  const loadOrdersRef = useRef(null);
  loadOrdersRef.current = async (page = 0, pageSize = PAGINATION_OPTIONS.PAGE_SIZE, agentId = null) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const currentRequestId = ++requestIdRef.current;

    setIsLoadingOrders(true);
    setOrdersError(null);

    try {
      // Always pull the full set — sort/paginate client-side from here on.
      const result = await getReceiptOrders({
        pageNumber: 1,
        pageSize: FULL_LOAD_PAGE_SIZE,
        agentId: agentId || undefined
      });

      if (currentRequestId !== requestIdRef.current) return;

      if (result.success) {
        const data = result.data;
        let items;
        let totalCount;
        if (Array.isArray(data)) {
          items = data;
          totalCount = data.length;
        } else {
          items = data.items || [];
          totalCount = data.totalCount || items.length;
          if (data.agencies) {
            setAgenciesWithCounts(data.agencies);
          }
        }
        setOrders(items);
        // Reset to the requested page, but clamp in case the new dataset is smaller.
        const maxPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1);
        setPagination({ page: Math.min(page, maxPage), pageSize, totalCount });
      } else {
        setOrders([]);
        setOrdersError(result.error || 'Failed to load orders');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (currentRequestId === requestIdRef.current) {
        setOrders([]);
        setOrdersError('Network error. Please try again.');
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoadingOrders(false);
      }
    }
  };

  const loadOrders = useCallback((page, pageSize, agentId) => {
    loadOrdersRef.current?.(page, pageSize, agentId);
  }, []);

  // Initial data load
  useEffect(() => {
    if (initialLoadDoneRef.current || authLoading) return;
    initialLoadDoneRef.current = true;

    if (isAdmin) {
      loadOrders(0, PAGINATION_OPTIONS.PAGE_SIZE, null);
    } else {
      loadPendingStatus();
    }
  }, [isAdmin, authLoading, loadOrders, loadPendingStatus]);

  // Focus input when admin view loads
  useEffect(() => {
    if (isAdmin && !authLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdmin, authLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle code input change
  const handleCodeChange = (e) => {
    const value = e.target.value;
    const isAdminCodePrefix = value.toLowerCase().startsWith(RECEIPT_VALIDATION.ADMIN_CODE_PREFIX);
    const maxLen = isAdminCodePrefix ? RECEIPT_VALIDATION.ADMIN_CODE_LENGTH : RECEIPT_VALIDATION.CODE_LENGTH;

    if (value.length > maxLen) return;

    setConfirmationCode(value);
    setLookupError('');
    if (lookupResult) {
      setLookupResult(null);
    }
  };

  // Handle Enter key for lookup
  const handleKeyDown = async (e) => {
    if (e.key === 'Enter' && codeIsValid && !isLookingUp && !lookupResult) {
      e.preventDefault();
      await handleLookup();
    }
  };

  // Lookup receipt by code
  const handleLookup = async () => {
    if (!codeIsValid || isLookingUp) return;

    setIsLookingUp(true);
    setLookupError('');
    setLookupResult(null);

    try {
      const result = await lookupReceiptByCode(confirmationCode.toUpperCase());

      if (result.success && result.data?.found) {
        setLookupResult(result.data);
      } else {
        setLookupError(result.data?.message || result.error || RECEIPT_MESSAGES.INVALID_CODE);
      }
    } catch (err) {
      setLookupError(RECEIPT_MESSAGES.NETWORK_ERROR);
    } finally {
      setIsLookingUp(false);
    }
  };

  // Confirm the receipt after lookup
  const handleConfirm = async () => {
    if (!lookupResult || isConfirming) return;

    setIsConfirming(true);
    setLookupError('');

    try {
      const result = await confirmPowerReceipt(confirmationCode.toUpperCase(), lookupResult.agentId);

      if (result.success) {
        const count = result.data?.confirmedCount || lookupResult.powerCount || 1;
        toast.success(RECEIPT_MESSAGES.SUCCESS(count));
        setConfirmationCode('');
        setLookupResult(null);
        // Refresh the orders list
        loadOrders(0, pagination.pageSize, null);
        setSelectedFilterAgencyId(null);
        setTimeout(() => inputRef.current?.focus(), 100);
      } else {
        setLookupError(result.error || RECEIPT_MESSAGES.INVALID_CODE);
      }
    } catch (err) {
      setLookupError(RECEIPT_MESSAGES.NETWORK_ERROR);
    } finally {
      setIsConfirming(false);
    }
  };

  // Clear and reset
  const handleClear = () => {
    setConfirmationCode('');
    setLookupError('');
    setLookupResult(null);
    setShowHelp(false);
    inputRef.current?.focus();
  };

  // Handle sort — toggles direction when clicking the same field, otherwise
  // switches to the new field with a sensible default direction. Kept as two
  // independent state updates (no nested setters) so StrictMode's double-invoke
  // of updater functions doesn't flip the toggle twice.
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    // Default directions: dates & counts desc (newest/most first), strings asc.
    setSortDirection(field === 'shipDate' || field === 'orderDate' || field === 'powerCount' ? 'desc' : 'asc');
  }, [sortField]);

  // Pagination is purely client-side now — no refetch on page/size change so
  // sort stays consistent across pages.
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPagination(prev => ({ ...prev, page: 0, pageSize: newPageSize }));
  }, []);

  // Sort the entire orders dataset (not just the current page) so header clicks
  // order rows across every page.
  const sortedOrders = useMemo(() => {
    if (orders.length === 0) return orders;

    return [...orders].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'shipDate' || sortField === 'orderDate') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [orders, sortField, sortDirection]);

  // Slice the sorted set for the current page.
  const paginatedOrders = useMemo(() => {
    const start = pagination.page * pagination.pageSize;
    return sortedOrders.slice(start, start + pagination.pageSize);
  }, [sortedOrders, pagination.page, pagination.pageSize]);

  // Handle agency filter change
  const handleAgencyFilterChange = useCallback((newAgencyId) => {
    setSelectedFilterAgencyId(newAgencyId);
    loadOrders(0, pagination.pageSize, newAgencyId);
  }, [loadOrders, pagination.pageSize]);

  // Handle user confirm button click (opens modal)
  const handleUserConfirmClick = useCallback(() => {
    setConfirmModalOpen(true);
  }, []);

  // Handle successful confirmation from modal
  const handleConfirmSuccess = useCallback(() => {
    loadPendingStatus();
  }, [loadPendingStatus]);

  // Admin: open detail modal for a clicked order
  const handleOrderRowClick = useCallback((order) => {
    setSelectedOrder(order);
  }, []);

  // Admin: from detail modal "Confirm Receipt", open the code-entry modal scoped to that agency
  const handleDetailConfirmClick = useCallback(({ agentId, agencyName }) => {
    setAdminConfirmContext({ agentId: agentId ?? null, agencyName: agencyName ?? '' });
  }, []);

  // Admin: refresh orders after a successful confirmation
  const handleAdminConfirmSuccess = useCallback(() => {
    setAdminConfirmContext(null);
    loadOrders(pagination.page, pagination.pageSize, selectedFilterAgencyId);
  }, [loadOrders, pagination.page, pagination.pageSize, selectedFilterAgencyId]);

  // Loading state
  if (authLoading) {
    return (
      <OathPageWrapper maxWidth="max-w-7xl">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <OathDualSpinner size="lg" className="mx-auto" />
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading Power Receipts...</p>
          </div>
        </div>
      </OathPageWrapper>
    );
  }

  // User View - Simple status and confirm button
  if (!isAdmin) {
    return (
      <OathPageWrapper maxWidth="max-w-7xl">
        <OathHeaderCard
          title="Power Receipts"
          subtitle="Confirm receipt of power shipments"
          icon={InboxArrowDownIcon}
        />

        <OathContentCard>
          {isLoadingStatus ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <OathSpinner size="lg" />
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  Checking for pending receipts...
                </p>
              </div>
            </div>
          ) : statusError ? (
            <div className="space-y-4">
              <OathAlert variant="danger">
                {statusError}
              </OathAlert>
              <div className="flex justify-center">
                <OathButton
                  variant="outline"
                  onClick={loadPendingStatus}
                >
                  Try Again
                </OathButton>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {pendingStatus?.hasPending || pendingStatus?.pendingCount > 0 ? (
                <>
                  <OathAlert variant="info" className="flex items-center gap-3">
                    <InboxArrowDownIcon className="h-6 w-6 flex-shrink-0" />
                    <div>
                      <p className="font-medium">{RECEIPT_MESSAGES.HAS_PENDING(pendingStatus?.pendingCount || 0)}</p>
                      <p className="text-sm mt-1 opacity-80">
                        {RECEIPT_MESSAGES.CODE_HELP}
                      </p>
                    </div>
                  </OathAlert>

                  <div className="flex justify-center">
                    <OathButton
                      onClick={handleUserConfirmClick}
                      icon={CheckCircleIcon}
                      size="lg"
                      className="px-8"
                    >
                      Confirm Receipt
                    </OathButton>
                  </div>
                </>
              ) : (
                <OathEmptyState
                  icon={InboxArrowDownIcon}
                  title="No Pending Receipts"
                  message={RECEIPT_MESSAGES.NO_PENDING}
                />
              )}
            </div>
          )}
        </OathContentCard>

        <ReceiptConfirmModal
          isOpen={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onSuccess={handleConfirmSuccess}
          agentId={null}
          agencyName=""
        />
      </OathPageWrapper>
    );
  }

  // Admin View - Code entry + Orders list
  return (
    <OathPageWrapper maxWidth="max-w-7xl">
      <OathHeaderCard
        title="Power Receipts"
        subtitle="Verify and confirm power shipment receipts"
        icon={InboxArrowDownIcon}
      />

      {/* Code Entry Section */}
      <OathContentCard className="mb-6">
        <div className="space-y-4">
          {/* Case-sensitive warning */}
          <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <ExclamationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {RECEIPT_MESSAGES.CASE_SENSITIVE_WARNING}
            </p>
          </div>

          {/* Code input */}
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between mb-2">
              <label
                htmlFor="confirmationCode"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Confirmation Code
              </label>
              <button
                type="button"
                onClick={() => setShowHelp(!showHelp)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label="Show help"
              >
                <QuestionMarkCircleIcon className="h-5 w-5" />
              </button>
            </div>

            {showHelp && (
              <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {RECEIPT_MESSAGES.CODE_HELP}
                </p>
              </div>
            )}

            <input
              ref={inputRef}
              id="confirmationCode"
              type={shouldMaskInput ? 'password' : 'text'}
              value={confirmationCode}
              onChange={handleCodeChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter 4-digit code"
              maxLength={shouldMaskInput ? RECEIPT_VALIDATION.ADMIN_CODE_LENGTH : RECEIPT_VALIDATION.CODE_LENGTH}
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck="false"
              disabled={isLookingUp || isConfirming}
              className={`w-full px-4 py-6 text-center text-4xl font-mono font-bold tracking-[0.5em] uppercase border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors placeholder:text-gray-300 dark:placeholder:text-gray-600 placeholder:tracking-normal placeholder:text-lg placeholder:font-normal ${(isLookingUp || isConfirming) ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{
                letterSpacing: shouldMaskInput ? 'normal' : '0.5em',
                fontSize: shouldMaskInput ? '1.5rem' : '2.25rem'
              }}
            />

            {/* Character count and valid indicator */}
            <div className="mt-2 flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
              <span>
                {confirmationCode.length > 0 && !shouldMaskInput && (
                  <>
                    {confirmationCode.length} / {RECEIPT_VALIDATION.CODE_LENGTH} characters
                  </>
                )}
              </span>
              {codeIsValid && !lookupResult && (
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <CheckCircleIcon className="h-4 w-4" />
                  Press Enter to lookup
                </span>
              )}
            </div>
          </div>

          {/* Error message */}
          {lookupError && (
            <div className="max-w-md mx-auto flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{lookupError}</p>
            </div>
          )}

          {/* Loading state */}
          {isLookingUp && (
            <div className="flex items-center justify-center py-4">
              <OathSpinner size="md" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Looking up receipt...</span>
            </div>
          )}

          {/* Lookup result - Receipt details */}
          {lookupResult && (
            <div className="max-w-lg mx-auto border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Receipt Details
                </h3>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Agency</p>
                    <p className="font-medium text-gray-900 dark:text-white">{lookupResult.agencyName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Order #</p>
                    <p className="font-medium text-gray-900 dark:text-white">{lookupResult.orderNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Assigned Date</p>
                    <p className="font-medium text-gray-900 dark:text-white">{formatDate(lookupResult.shipDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Power Count</p>
                    <p className="font-medium text-gray-900 dark:text-white">{lookupResult.powerCount}</p>
                  </div>
                </div>

                {/* Power list */}
                {lookupResult.powers && lookupResult.powers.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Powers in Shipment</p>
                    <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Power #</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Status</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Denomination</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {lookupResult.powers.map((power) => {
                            const statusInfo = getPowerStatusBadgeInfo(power.status);
                            return (
                              <tr key={power.powerId}>
                                <td className="px-3 py-2 text-gray-900 dark:text-white font-mono">{power.powerNumber}</td>
                                <td className="px-3 py-2">
                                  <span className={statusInfo.classes}>
                                    {power.statusDescription || statusInfo.shortLabel}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">{formatCurrency(power.denomination)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm buttons */}
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <OathButton
                  variant="outline"
                  onClick={handleClear}
                  disabled={isConfirming}
                >
                  Cancel
                </OathButton>
                <OathButton
                  onClick={handleConfirm}
                  disabled={isConfirming}
                  icon={isConfirming ? null : CheckCircleIcon}
                >
                  {isConfirming ? (
                    <span className="flex items-center gap-2">
                      <OathSpinner size="sm" />
                      Confirming...
                    </span>
                  ) : (
                    'Confirm Receipt'
                  )}
                </OathButton>
              </div>
            </div>
          )}
        </div>
      </OathContentCard>

      {/* Pending Orders List */}
      <OathContentCard>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Pending Orders
        </h3>

        {/* Agency Filter */}
        {agenciesWithCounts.length > 1 && (
          <div className="mb-4 sm:mb-5 sm:p-4 sm:bg-gray-50 sm:dark:bg-gray-800/50 sm:rounded-xl sm:border sm:border-gray-200 sm:dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <BuildingOfficeIcon className="h-5 w-5" />
                <span className="text-sm font-medium">Agency</span>
              </div>
              <div className="relative flex-1 sm:max-w-md">
                <BuildingOfficeIcon className="sm:hidden absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <select
                  value={selectedFilterAgencyId || 'all'}
                  onChange={(e) => handleAgencyFilterChange(e.target.value === 'all' ? null : parseInt(e.target.value))}
                  className="w-full pl-9 sm:pl-4 pr-10 py-2.5 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors appearance-none cursor-pointer"
                >
                  <option value="all">All Agencies ({agenciesWithCounts.reduce((sum, a) => sum + a.pendingCount, 0)})</option>
                  {agenciesWithCounts.map((agency) => (
                    <option key={agency.agentId} value={agency.agentId}>
                      {agency.agentId} - {agency.agencyName} ({agency.pendingCount})
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {isLoadingOrders ? (
          isMobile ? <MobileLoadingSkeleton /> : <TableLoadingSkeleton />
        ) : ordersError ? (
          <div className="space-y-4 py-8">
            <OathAlert variant="danger">
              {ordersError}
            </OathAlert>
            <div className="flex justify-center">
              <OathButton
                variant="outline"
                onClick={() => loadOrders(0, pagination.pageSize, selectedFilterAgencyId)}
              >
                Try Again
              </OathButton>
            </div>
          </div>
        ) : orders.length === 0 ? (
          <OathEmptyState
            icon={InboxArrowDownIcon}
            title="No pending orders"
            message={selectedFilterAgencyId
              ? 'No pending power shipments for this agency.'
              : 'There are no pending power shipments awaiting confirmation.'
            }
          />
        ) : isMobile ? (
          <div className="space-y-3">
            {paginatedOrders.map((order) => (
              <OrderMobileCard
                key={order.orderId}
                order={order}
                onClick={handleOrderRowClick}
              />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <SortableHeader label="Agency" field="agencyName" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Order #" field="orderNumber" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader label="Assigned Date" field="shipDate" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                  {RECEIPT_FEATURES.SHOW_POWER_COUNT && (
                    <SortableHeader label="# Powers" field="powerCount" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} align="center" />
                  )}
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                {paginatedOrders.map((order) => (
                  <OrderTableRow
                    key={order.orderId}
                    order={order}
                    onClick={handleOrderRowClick}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {orders.length > 0 && (
          <Pagination
            page={pagination.page || 0}
            pageSize={pagination.pageSize || PAGINATION_OPTIONS.PAGE_SIZE}
            totalCount={pagination.totalCount || 0}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </OathContentCard>

      {RECEIPT_FEATURES.ORDER_DRILLDOWN && (
        <ReceiptOrderDetailModal
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          orderId={selectedOrder?.orderId}
          agentId={selectedOrder?.agentId}
          agencyName={selectedOrder?.agencyName}
          onConfirmClick={handleDetailConfirmClick}
        />
      )}

      <ReceiptConfirmModal
        isOpen={!!adminConfirmContext}
        onClose={() => setAdminConfirmContext(null)}
        onSuccess={handleAdminConfirmSuccess}
        agentId={adminConfirmContext?.agentId ?? null}
        agencyName={adminConfirmContext?.agencyName ?? ''}
      />
    </OathPageWrapper>
  );
};

export default withErrorBoundary(PowerReceiptsPage, {
  title: 'Power Receipts Error',
  message: 'An error occurred in the Power Receipts module. Please try again.'
});
